import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import configData from '../../configure';
import './BulkImportHub.css';
import { useAuth } from "../../AuthContext";
import { Loader2 } from 'lucide-react';
import {
    FaCloudUploadAlt,
    FaFileCsv, FaExclamationCircle, FaDownload,
    FaEye
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import StudentBulkImport from './StudentBulkImport';


// --- Helper Functions ---
const generateUUID = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });

// Updated: Generate Admission ID starting from properly padded number
const createGeneratedId = (typeCode, index, startingNumber = 1) => {
    const year = new Date().getFullYear().toString().slice(-2); // e.g., "26"
    const admissionNumber = String(Number(startingNumber) + (index - 1)).padStart(3, '0');
    return `${typeCode}${year}${admissionNumber}`;
};

const parseDate = (dateStr) => {
    if (dateStr === null || dateStr === undefined || dateStr === '') return null;

    const value = String(dateStr).trim();
    if (!value) return null;

    if (/^\d+(\.\d+)?$/.test(value)) {
        const excelSerial = Number(value);
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        const parsed = new Date(excelEpoch.getTime() + excelSerial * 86400000);
        return isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(value)) {
        const [day, month, yearRaw] = value.split('/');
        const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
        const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        return isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toISOString();
};

const normalizePhone = (value) => String(value || '').replace(/\D/g, '').slice(-10);

const createGeneratedEmail = (firstName, lastName) => {
    const fn = String(firstName || 'user').toLowerCase().replace(/[^a-z0-9]/g, '');
    const ln = String(lastName || 'name').toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${fn}.${ln}@sevabharathi.com`;
};

const createGeneratedParentEmail = (name, relation, rowNumber) => {
    const slug = String(name || relation || 'parent')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '.')
        .replace(/^\.+|\.+$/g, '') || 'parent';
    return `${slug}.${relation.toLowerCase()}.r${rowNumber}@sevabharathi.com`;
};

const normalizeGender = (gender) => {
    const value = String(gender || '').trim().toUpperCase();
    if (value === 'MALE' || value === 'FEMALE' || value === 'OTHER') return value;
    return 'OTHER';
};

const normalizeFacultyType = (facultyType) => {
    const value = String(facultyType || '').trim().toUpperCase().replace(/\s+/g, '_');
    const allowed = [
        'PRIMARY_TEACHER', 'HIGH_SCHOOL_TEACHER', 'FULL_PROFESSOR',
        'SAMANVAYKARTA', 'PARYAVEKSHAK', 'NIRVAHAK'
    ];
    if (allowed.includes(value)) {
        return value;
    }
    return 'HIGH_SCHOOL_TEACHER';
};

const STUDENT_TEMPLATE_COLUMNS = [
    { label: 'First Name', required: true },
    { label: 'Last Name', required: true },
    { label: 'Gender', required: false },
    { label: 'Class', required: false },
    { label: 'Father Name', required: true },
    { label: 'Father Occupation', required: false },
    { label: 'Mother Name', required: true },
    { label: 'Mother Occupation', required: false },
    { label: 'Date of Birth', required: false },
    { label: 'Mobile No', required: true },
    { label: 'Area', required: false },
    { label: 'Caste', required: false },
    { label: 'School Name', required: false },
];

const STUDENT_IMPORT_FIELD_ALIASES = {
    firstName: ['first name', 'firstname'],
    lastName: ['last name', 'lastname'],
    gender: ['gender', 'sex'],
    className: ['class', 'class name'],
    fatherName: ['father name', 'fathername'],
    fatherOccupation: ['father occupation', 'occupation', 'father occupation/name'],
    motherName: ['mother name', 'mothername'],
    motherOccupation: ['mother occupation', 'occupatin', 'mother occupation/name'],
    dateOfBirth: ['date of birth', 'dob', 'birth date'],
    mobileNo: ['mobile no', 'mobile number', 'phone number', 'phone', 'contact number'],
    area: ['area', 'location', 'address area'],
    caste: ['caste', 'cast', 'category'],
    schoolName: ['school name', 'previous school', 'school'],
    singleParent: ['single parent'],
};

const normalizeImportHeader = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getStudentFieldValue = (row, aliases) => {
    const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
        acc[normalizeImportHeader(key)] = value;
        return acc;
    }, {});

    for (const alias of aliases) {
        const normalizedAlias = normalizeImportHeader(alias);
        if (Object.prototype.hasOwnProperty.call(normalizedRow, normalizedAlias)) {
            return normalizedRow[normalizedAlias];
        }
    }

    return '';
};

const parseCsvTextToObjects = (csvText) => {
    const parsed = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => String(header || '').trim(),
    });

    if (parsed.errors?.length) {
        const firstError = parsed.errors[0];
        throw new Error(firstError.message || 'Failed to parse CSV file');
    }

    return parsed.data || [];
};

const FIELD_GUIDE = {
    students: [
        { label: 'First Name', type: 'Required', desc: 'Student\'s first name.', mandatory: true },
        { label: 'Last Name', type: 'Required', desc: 'Student\'s last name.', mandatory: true },
        { label: 'Gender', type: 'Optional', desc: 'Male, Female, or Other.', mandatory: false },
        { label: 'Class', type: 'Optional', desc: 'Reference only. Student will be assigned to the selected class.', mandatory: false },
        { label: 'Father Name', type: 'Required', desc: 'Full name of the father.', mandatory: true },
        { label: 'Father Occupation', type: 'Optional', desc: 'Profession of the father.', mandatory: false },
        { label: 'Mother Name', type: 'Required', desc: 'Full name of the mother.', mandatory: true },
        { label: 'Mother Occupation', type: 'Optional', desc: 'Profession of the mother.', mandatory: false },
        { label: 'Date of Birth', type: 'Optional', desc: 'Student date of birth.', mandatory: false },
        { label: 'Mobile No', type: 'Required', desc: '10-digit mobile number.', mandatory: true },
        { label: 'Area', type: 'Optional', desc: 'Area or locality.', mandatory: false },
        { label: 'Caste', type: 'Optional', desc: 'Maps to category (SC, BC, OC, etc).', mandatory: false },
        { label: 'School Name', type: 'Optional', desc: 'Goes to Additional Details.', mandatory: false },
    ],
    faculty: [
        { label: 'First Name', type: 'Required', desc: "Teacher's first name.", mandatory: true },
        { label: 'Last Name', type: 'Required', desc: "Teacher's last name.", mandatory: true },
        { label: 'Email', type: 'Required', desc: 'Official email for login.', mandatory: true },
        { label: 'Gender', type: 'Required', desc: 'Male, Female, or Other.', mandatory: true },
        { label: 'Date of Birth', type: 'Required', desc: 'Teacher date of birth (DD/MM/YYYY).', mandatory: true },
        { label: 'Supervisor ID / Phone', type: 'Optional', desc: "Supervisor's phone number, email, or employee ID.", mandatory: false }
    ]
};

const IMPORT_OPTIONS = [
    { id: 'classes', label: 'Classes & Sections', desc: 'Import multiple classes and sections at once.', step: 1, startHere: true },
    { id: 'courses', label: 'Courses (Subjects)', desc: 'Bulk add subjects/courses to the curriculum.', step: 2 },
    { id: 'students', label: 'Students', desc: 'Register students and create their accounts.', step: 3 },
    { id: 'faculty', label: 'Faculty', desc: 'Onboard teachers and assign departments.', step: 4 },
    { id: 'staff', label: 'Staff', desc: 'Add administrative and support staff members.', step: 5 },
    { id: 'fees', label: 'Fee Structures', desc: 'Define fee plans for specific classes.', step: 6 },
];

const STUDENTS_TEMPLATE_PREVIEW = STUDENT_TEMPLATE_COLUMNS;

function BulkImportHub({ orgId: propOrgId }) {
    const { axios, token, user, orgId: contextOrgId, parentOrgId } = useAuth();
    const orgId = propOrgId || contextOrgId || user?.orgId;
    const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
    const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';
    const navigate = useNavigate();
    const location = useLocation();
    const preselectedClassId = location.state?.selectedClass || '';

    // --- UI STATE ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [importType, setImportType] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('idle');
    const [stats, setStats] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedPreview, setExpandedPreview] = useState('');
    const [completedMap, setCompletedMap] = useState(() => {
        const saved = window.localStorage.getItem('bulkImport:completedMap');
        if (!saved) return {};
        try {
            return JSON.parse(saved);
        } catch {
            return {};
        }
    });

    // --- DATA STATE ---
    const [classes, setClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState(preselectedClassId);
    const [file, setFile] = useState(null);

    const filteredImportOptions = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return IMPORT_OPTIONS.filter((opt) => {
            if (isSmallOrg && opt.id === 'staff') return false;
            const matchesSearch =
                !q ||
                opt.label.toLowerCase().includes(q) ||
                opt.desc.toLowerCase().includes(q);
            return matchesSearch;
        });
    }, [searchTerm, isSmallOrg]);

    // Student import handled by dedicated component
    const [studentModalOpen, setStudentModalOpen] = useState(false);

    useEffect(() => {
        const type = location.state?.importType;
        if (type) {
            if (type === 'students') {
                setStudentModalOpen(true);
            } else {
                setImportType(type);
                setIsModalOpen(true);
                setFile(null);
                setStats(null);
                setUploadStatus('idle');
            }
        }
    }, [location.state]);

    // --- CONFIGURATION ---
    const formatDate = (isoDate) => {
        if (!isoDate) return 'No imports yet';
        const date = new Date(isoDate);
        return `${date.toLocaleDateString()} • ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    useEffect(() => {
        const fetchClasses = async () => {
            if (!orgId) {
                setClasses([]);
                return;
            }
            try {
                const { data } = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}`);
                setClasses(data);
            } catch (err) { console.error("Failed to load classes", err); }
        };

        setSelectedClassId(''); // Reset selection when org changes
        fetchClasses();
    }, [orgId, axios]);

    // --- EVENT HANDLERS ---
    const handleOpenImport = (typeId) => {
        if (typeId === 'students') {
            setStudentModalOpen(true);
            return;
        }
        setImportType(typeId);
        setIsModalOpen(true);
        setFile(null);
        setStats(null);
        setUploadStatus('idle');
        setSelectedClassId(preselectedClassId);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setImportType(null);
        setFile(null);
        setStats(null);
        setUploadStatus('idle');
        setSelectedClassId(preselectedClassId);
    };

    useEffect(() => {
        setSelectedClassId(preselectedClassId);
    }, [preselectedClassId]);

    const markImportCompleted = (typeId, successCount = 0) => {
        // Functionality removed as per user request
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const lowerName = selectedFile.name.toLowerCase();
            const isStudentSheet = importType === 'students' && (lowerName.endsWith('.csv') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls'));
            const isCsvOnly = selectedFile.type === 'text/csv' || lowerName.endsWith('.csv');

            if (!(isStudentSheet || isCsvOnly)) {
                toast.error(importType === 'students'
                    ? 'Please upload a valid CSV or Excel file.'
                    : 'Please upload a valid CSV file.');
                return;
            }
            setFile(selectedFile);
        }
    };

    const downloadTemplate = (type) => {
        const targetType = type || importType;
        if (!targetType) return;

        let content = "", filename = `${targetType}_template.csv`;

        if (targetType === 'classes') {
            content = "Class Name,Section\nClass 10,A\nClass 10,B";
        } else if (targetType === 'fees') {
            content = "AcademicYear,TermName,StartDate,EndDate,DueDate,TuitionFee,ExamFee,AdmissionFee,SmartClassFee,ComputerFee,SportsFee,LibraryFee\n2025-2026,Term 1,2025-06-01,2025-09-30,2025-06-15,12000,500,5000,1000,500,200,100";
        } else if (targetType === 'courses') {
            content = "Course Name,Course Code,Course Type,Description,Topics (semicolon separated)\nMathematics,MATH101,Theory,Introduction to Calculus,Algebra;Geometry";
        } else if (targetType === 'students') {
            const headers = STUDENT_TEMPLATE_COLUMNS.map((field) => field.label).join(',');
            const sampleValues = [
                'Prasanna',
                'Pole',
                'Female',
                'Class 4',
                'Gopi',
                'Painter',
                'Sumaltha',
                'House Wife',
                '20/04/2015',
                '9553133517',
                'Anna Nagar',
                'SC',
                'Little Flowers School'
            ].join(',');
            content = `${headers}\n${sampleValues}`;
        } else if (targetType === 'faculty') {
            const headers = 'firstName,lastName,email,phoneNumber,gender,dob,placeOfBirth,bloodGroup,nationality,religion,category,currentAddress,currentAddress2,city,state,pincode,alternatePhoneNumber,employeeId,designation,department,joiningDate,facultyType,lastFourDigitsOfAadhaarNumber,supervisorId';
            const row1 = 'Anita,Sharma,faculty5@gmail.com,9876543201,FEMALE,1975-03-10,Delhi,A_POSITIVE,Indian,Hindu,General,123 Street,,Delhi,Delhi,110001,9876543213,FAC001,Professor,Computer Science,2010-06-15,LECTURER,1234,9347718281';
            content = `${headers}\n${row1}`;
        } else if (targetType === 'staff') {
            const headers = 'firstName,lastName,email,phoneNumber,gender,dob,placeOfBirth,bloodGroup,nationality,religion,category,currentAddress,currentAddress2,city,state,pincode,alternatePhoneNumber,employeeId,designation,department,joiningDate,staffContractType,responsibilities,identificationMark,drivingLicenseNumber,drivingLicenseValidity,drivingExperienceYears,lastFourDigitsOfAadhaarNumber';
            const row1 = 'Ramesh,Kumar,ramesh.kumar@example.com,9876543201,MALE,1980-03-10,Delhi,A_POSITIVE,Indian,Hindu,General,123 Street,,Delhi,Delhi,110001,9876543211,EMP2001,Driver,Transport,2020-01-15,PERMANENT,Driving;Maintenance,Mole on left cheek,DL123456,2025-01-15,10,1234';
            content = `${headers}\n${row1}`;
        }

        if (!content) { console.warn("No template content for type:", targetType); return; }
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // --- UPLOAD PROCESS ---
    const handleUpload = async () => {
        if (!file || !orgId) return;
        if (importType === 'fees' && !selectedClassId) {
            toast.warn('Please select a class first.');
            return;
        }

        setUploadStatus('processing');
        try {
            const text = await file.text();
            if (importType === 'classes') await processClasses(text);
            else if (importType === 'fees') await processFees(text);
            else if (importType === 'courses') await processCourses(text);
            else if (importType === 'faculty') await processFaculty(text);
            else if (importType === 'staff') await processStaff(text);
        } catch (err) {
            console.error('Processing Error', err);
            toast.error('An unexpected error occurred: ' + err.message);
            setUploadStatus('error');
        }
    };

    // --- PROCESSING LOGIC ---
    const processClasses = async (csvText) => {
        const rows = csvText.trim().split('\n').slice(1);
        let success = 0, fail = 0;
        for (const row of rows) {
            if (!row.trim()) continue;
            const cols = row.split(',').map(c => c.trim());
            if (cols.length < 2) continue;
            try {
                await axios.post(configData.CREATE_CLASS, {
                    className: cols[0],
                    sectionName: cols[1],
                    orgId,
                    courseIds: ["string"]
                }, { headers: { Authorization: token } });
                success++;
            } catch (e) { fail++; console.error("Class Import Error", e); }
        }
        setStats({ success, fail });
        markImportCompleted('classes', success);
        setUploadStatus('complete');
    };

    const processCourses = async (csvText) => {
        const rows = csvText.trim().split('\n').slice(1);
        let success = 0, fail = 0;
        for (const row of rows) {
            if (!row.trim()) continue;
            const parts = row.split(',').map(p => p.trim());
            if (parts.length < 2) continue;
            try {
                const payload = {
                    orgId,
                    courseCode: parts[1],
                    courseType: parts[2]?.toLowerCase() || 'theory',
                    courseDetails: {
                        courseName: parts[0],
                        courseDescription: parts[3] || 'No description provided',
                        topics: parts.slice(4).join(',').split(';').map(t => t.trim()).filter(t => t)
                    }
                };
                await axios.post(configData.CREATE_SUBJECT, payload, {
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
                });
                success++;
            } catch (e) { fail++; console.error("Course Import Error", e); }
        }
        setStats({ success, fail });
        markImportCompleted('courses', success);
        setUploadStatus('complete');
    };

    const processFees = async (csvText) => {
        const rows = csvText.split('\n').map(r => r.trim()).filter(r => r);
        if (rows.length < 2) return;
        const headers = rows[0].split(',').map(h => h.trim());
        const dataRows = rows.slice(1);
        const statsObj = { success: 0, duplicates: 0, failed: 0, errors: [] };
        const groupedData = {};

        const selectedClass = classes.find(c => c.classId === selectedClassId);
        const className = selectedClass ? `${selectedClass.className} ${selectedClass.sectionName}` : "Unknown";

        for (let i = 0; i < dataRows.length; i++) {
            const cols = dataRows[i].split(',').map(c => c.trim());
            if (cols.length < 5) continue;
            const academicYear = cols[0];
            const startDate = parseDate(cols[2]);
            const endDate = parseDate(cols[3]);
            const dueDate = parseDate(cols[4]);

            if (!startDate || !endDate || !dueDate) {
                statsObj.errors.push({ row: i + 2, reason: "Invalid dates" });
                continue;
            }

            const key = `${selectedClassId}|${academicYear}`;
            if (!groupedData[key]) groupedData[key] = { classId: selectedClassId, academicYear, className, terms: [] };

            const feeBreakdown = {};
            for (let j = 5; j < headers.length; j++) {
                const amount = parseFloat(cols[j]);
                if (!isNaN(amount) && amount > 0) feeBreakdown[headers[j]] = amount;
            }
            groupedData[key].terms.push({ description: cols[1], startDate, endDate, dueDate, feeBreakdown });
        }

        for (const key in groupedData) {
            const item = groupedData[key];
            try {
                const existingRes = await axios.get(`${configData.GET_FEE_STRUCTURE}/class/${item.classId}?academicYear=${item.academicYear}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (existingRes.data.find(f => f.academicYear === item.academicYear)) {
                    statsObj.duplicates++;
                    continue;
                }

                const payload = {
                    createdBy: user?.email || "admin",
                    createdAt: new Date().toISOString(),
                    lastUpdatedBy: user?.email || "admin",
                    lastUpdatedAt: new Date().toISOString(),
                    feePlanId: generateUUID(),
                    academicYear: item.academicYear,
                    active: true,
                    classId: item.classId,
                    description: item.className,
                    feeInstallments: item.terms.map(t => ({
                        installmentId: generateUUID(),
                        description: t.description,
                        installmentType: "TERM",
                        startDate: t.startDate,
                        endDate: t.endDate,
                        dueDate: t.dueDate,
                        feeBreakdown: t.feeBreakdown,
                        totalFee: Object.values(t.feeBreakdown).reduce((a, b) => a + b, 0),
                        lateFee: 0
                    })),
                    orgId
                };
                const postRes = await axios.post(configData.GET_FEE_STRUCTURE, payload, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json, application/hal+json' }
                });
                const createdFeeStructureId = postRes.data?.feeStructureId || postRes.data?.id;
                if (createdFeeStructureId) {
                    console.log('Created feeStructureId:', createdFeeStructureId);
                }
                statsObj.success++;
            } catch (e) {
                statsObj.failed++;
                statsObj.errors.push({ row: "N/A", reason: e.response?.data?.message || e.message });
            }
        }
        setStats(statsObj);
        markImportCompleted('fees', statsObj.success || 0);
        setUploadStatus('complete');
    };

    // Reuse combined user upload logic
    const processUserUpload = async (csvText, userType, mapRowToPayload) => {
        const parsed = Papa.parse(csvText, { skipEmptyLines: 'greedy' });
        if (parsed.errors?.length) {
            console.warn("CSV parsing warning:", parsed.errors);
        }
        const allRows = parsed.data || [];
        const dataRows = allRows.slice(1); // skip header row

        const statsObj = { total: 0, success: 0, duplicates: 0, failed: 0, errors: [] };
        const itemsToProcess = dataRows.map((row, idx) => ({
            row: idx + 2,
            data: row.map(s => String(s || '').trim())
        }));
        statsObj.total = itemsToProcess.length;

        // Compile faculty map to resolve supervisor references
        const facultyMap = new Map();
        if (userType === 'FACULTY' && orgId) {
            try {
                const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
                    params: { userType: 'FACULTY', page: 1, pageSize: 2000 },
                    headers: { accept: 'application/hal+json' }
                });
                const existingFaculty = response.data?.items || response.data?.content || [];
                existingFaculty.forEach(f => {
                    const userId = f.user?.userId || f.userId;
                    const email = f.user?.email || f.email;
                    const employeeId = f.facultyInfo?.employeeId || f.employeeId;
                    const phoneNumber = f.user?.phoneNumber || f.phoneNumber;

                    if (userId) facultyMap.set(userId.toLowerCase().trim(), userId);
                    if (email) facultyMap.set(email.toLowerCase().trim(), userId);
                    if (employeeId) facultyMap.set(employeeId.toLowerCase().trim(), userId);
                    if (phoneNumber) {
                        const rawPhone = phoneNumber.toLowerCase().trim();
                        facultyMap.set(rawPhone, userId);
                        const normPhone = normalizePhone(phoneNumber);
                        if (normPhone) facultyMap.set(normPhone, userId);
                    }
                });
            } catch (err) {
                console.error("Failed to fetch existing faculty for mapping supervisorIds", err);
            }
        }

        let batchIndex = 1;
        for (const item of itemsToProcess) {
            try {
                const { payload, email, afterCreate } = mapRowToPayload(item.data, item.row, batchIndex++, facultyMap);
                if (!email) throw new Error("Invalid Email in row");
                payload.user.email = email;

                const response = await axios.post(configData.REGISTER_USER, payload, {
                    headers: {
                        'accept': 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                });

                if (response.status === 200 || response.status === 201) {
                    const newUserId = response.data?.userId || response.data?.user?.userId;
                    if (newUserId && afterCreate) {
                        await afterCreate(newUserId);
                    }

                    if (newUserId && userType === 'FACULTY') {
                        const createdUser = response.data?.user || response.data;
                        const uId = createdUser.userId || newUserId;
                        const uEmail = createdUser.email || email;
                        const uPhone = createdUser.phoneNumber || payload.user?.phoneNumber;
                        const uEmpId = payload.facultyInfo?.employeeId;

                        if (uId) facultyMap.set(uId.toLowerCase().trim(), uId);
                        if (uEmail) facultyMap.set(uEmail.toLowerCase().trim(), uId);
                        if (uPhone) {
                            const rawPhone = uPhone.toLowerCase().trim();
                            facultyMap.set(rawPhone, uId);
                            const normPhone = normalizePhone(uPhone);
                            if (normPhone) facultyMap.set(normPhone, uId);
                        }
                        if (uEmpId) facultyMap.set(uEmpId.toLowerCase().trim(), uId);
                    }

                    statsObj.success++;
                    if (newUserId) sendWelcomeEmail(newUserId, email, userType);
                } else {
                    throw new Error(`Server returned ${response.status}`);
                }
            } catch (err) {
                console.error("Import API Error:", err.response?.data || err.message);
                statsObj.failed++;
                statsObj.errors.push({ row: item.row, reason: err.response?.data?.message || err.message });
            }
        }
        setStats(statsObj);
        markImportCompleted(userType.toLowerCase(), statsObj.success || 0);
        setUploadStatus('complete');

        if (statsObj.success > 0) {
            toast.success(`Successfully imported ${statsObj.success} ${userType}(s).`);
        }
        if (statsObj.failed > 0) {
            toast.error(`Failed to import ${statsObj.failed} ${userType}(s). Check the error log.`);
        }
    };

    const sendWelcomeEmail = async (userId, email, type) => {
        if (!userId || !token) return;
        try {
            const emailData = {
                subject: "Welcome to Egurukul - Your Account Details",
                messageBody: `Welcome to Egurukul!\n\nYour account has been successfully created.\n\nLogin Details:\nUsername: ${email}\nPassword: egurukul@123456\n\nPlease login and change your password immediately.`,
                userType: type,
                specificUserIds: [userId],
            };
            const fd = new FormData();
            fd.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));
            await axios.post(configData.SEND_EMAIL_NOTIFICATION, fd, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
        } catch (e) { console.warn("Welcome email failed", e); }
    };

    const processFaculty = async (text) => processUserUpload(text, "FACULTY", (values, rowNumber, batchIndex, facultyMap) => {
        if (values.length < 5) throw new Error("Missing required columns: First Name, Last Name, Email, Phone, Gender");

        const firstName = String(values[0] || '').trim();
        const lastName = String(values[1] || '').trim();
        let email = String(values[2] || '').trim();
        const phoneNumber = String(values[3] || '').trim();
        const genderRaw = String(values[4] || '').trim();
        const dobRaw = String(values[5] || '').trim();
        const hasDepartmentColumn = values.length >= 24;
        const department = hasDepartmentColumn ? String(values[19] || '').trim() : '';
        const joiningDateRaw = hasDepartmentColumn ? values[20] : values[19];
        const facultyTypeRaw = hasDepartmentColumn ? values[21] : values[20];
        const aadhaarRaw = hasDepartmentColumn ? values[22] : values[21];
        const supervisorId = String((hasDepartmentColumn ? values[23] : values[22]) || '').trim() || null;

        if (!firstName || !lastName) {
            throw new Error("First Name and Last Name are required");
        }
        if (!email) {
            email = createGeneratedEmail(firstName, lastName);
        }
        if (!phoneNumber) {
            throw new Error("Phone Number is required");
        }
        if (!genderRaw) {
            throw new Error("Gender is required");
        }
        const gender = normalizeGender(genderRaw);

        const dob = dobRaw ? parseDate(dobRaw) : null;
        if (dobRaw && !dob) {
            throw new Error("Date of Birth must be valid when provided (format: DD/MM/YYYY or YYYY-MM-DD)");
        }

        const employeeId = String(values[17] || '').trim() || createGeneratedId('F', batchIndex);

        let resolvedSupervisorId = null;
        if (supervisorId) {
            const cleanSup = supervisorId.toLowerCase().trim();
            const normSup = normalizePhone(supervisorId);
            resolvedSupervisorId = facultyMap.get(cleanSup) || (normSup ? facultyMap.get(normSup) : null) || supervisorId;
        }

        return {
            email: email,
            payload: {
                user: {
                    creationTime: new Date().toISOString(),
                    createdBy: "admin",
                    lastUpdatedTime: new Date().toISOString(),
                    lastUpdatedBy: "admin",
                    orgId,
                    userType: "FACULTY",
                    roleId: "ROLE_FACULTY",
                    userStatus: "ACTIVE",
                    email: email,
                    phoneNumber: phoneNumber,
                    firstName: firstName,
                    lastName: lastName,
                    gender: gender,
                    dateOfBirth: dob,
                    placeOfBirth: values[6] || null,
                    bloodGroup: values[7] || null,
                    nationality: values[8] || "Indian",
                    religion: values[9] || null,
                    category: values[10] || null,
                    lastFourDigitsOfAadhaarNumber: aadhaarRaw ? parseInt(aadhaarRaw, 10) : null,
                    addressDetails: [{
                        addressType: "CURRENT",
                        addressLine1: values[11] || "Unknown",
                        addressLine2: values[12] || "",
                        city: values[13] || "Unknown",
                        state: values[14] || "Unknown",
                        pinCode: values[15] || "000000",
                        country: "India",
                        alternatePhoneNumber: values[16] || ""
                    }]
                },
                facultyInfo: {
                    creationTime: new Date().toISOString(),
                    createdBy: "admin",
                    lastUpdatedTime: new Date().toISOString(),
                    lastUpdatedBy: "admin",
                    employeeId: employeeId,
                    designation: values[18] || "",
                    department: department || "",
                    joiningDate: parseDate(joiningDateRaw) || new Date().toISOString(),
                    facultyType: normalizeFacultyType(facultyTypeRaw),
                    supervisorId: resolvedSupervisorId
                }
            }
        };
    });

    const processStaff = async (text) => processUserUpload(text, "STAFF", (values, rowNumber, batchIndex) => {
        if (values.length < 3) throw new Error("Missing columns (minimum 3 required)");
        const firstName = values[0];
        const lastName = values[1];
        const email = createGeneratedEmail(firstName, lastName);
        const generatedEmployeeId = createGeneratedId('T', batchIndex);
        return {
            email: email,
            payload: {
                user: {
                    creationTime: new Date().toISOString(),
                    createdBy: "admin",
                    lastUpdatedTime: new Date().toISOString(),
                    lastUpdatedBy: "admin",
                    orgId,
                    userType: "STAFF",
                    roleId: "ROLE_STAFF",
                    userStatus: "ACTIVE",
                    email: email,
                    phoneNumber: values[3],
                    firstName: values[0],
                    lastName: values[1],
                    gender: values[4] ? values[4].toUpperCase() : "UNKNOWN",
                    dateOfBirth: parseDate(values[5]),
                    placeOfBirth: values[6],
                    bloodGroup: values[7] || null,
                    nationality: values[8] || "Indian",
                    religion: values[9] || null,
                    category: values[10] || null,
                    lastFourDigitsOfAadhaarNumber: values[27] ? parseInt(values[27], 10) : null,
                    addressDetails: [{
                        addressType: "CURRENT",
                        addressLine1: values[11] || "Unknown",
                        addressLine2: values[12] || "",
                        city: values[13] || "Unknown",
                        state: values[14] || "Unknown",
                        pinCode: values[15] || "000000",
                        country: "India",
                        alternatePhoneNumber: values[16] || ""
                    }]
                },
                staffInfo: {
                    creationTime: new Date().toISOString(),
                    createdBy: "admin",
                    lastUpdatedTime: new Date().toISOString(),
                    lastUpdatedBy: "admin",
                    employeeId: generatedEmployeeId,
                    designation: values[18] || "Staff",
                    department: values[19] || "General",
                    joiningDate: parseDate(values[20]) || new Date().toISOString(),
                    staffContractType: values[21] || "PERMANENT",
                    responsibilities: values[22] ? values[22].split(';') : [],
                    identificationMark: values[23],
                    drivingLicenseNumber: values[24],
                    drivingLicenseValidity: parseDate(values[25]),
                    drivingExperienceYears: values[26] || 0
                }
            }
        };
    });

    return (
        <div className="bulk-hub-container">
            <div className="hub-header">
                <h1 className="hub-title">Bulk Import Hub</h1>
                <p className="hub-subtitle">Quickly import your data using standard templates.</p>
            </div>


            <div className="bulk-filter-row">
                <div className="bulk-search-box">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search import entities....."
                    />
                </div>
            </div>

            <div className="import-list-container">
                <table className="import-table">
                    <thead>
                        <tr>
                            <th>Import Entity</th>
                            <th>Description</th>
                            <th>Template</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredImportOptions.map(opt => {
                            const completedInfo = completedMap[opt.id];
                            const isCompleted = completedInfo?.status === 'completed';
                            return (
                                <React.Fragment key={opt.id}>
                                    <tr
                                        className={expandedPreview === opt.id ? 'highlight-row' : ''}
                                        onClick={() => {
                                            if (opt.id === 'students') {
                                                setExpandedPreview((prev) => (prev === 'students' ? '' : 'students'));
                                            }
                                        }}
                                    >
                                        <td className="entity-cell">
                                            <div className="entity-name-wrapper">
                                                <div className="entity-label-group">
                                                    <span className="entity-name">{opt.label}</span>
                                                </div>
                                                <span className="entity-step-label">Step {opt.step}{opt.startHere ? ' — Start here' : ''}</span>
                                            </div>
                                        </td>
                                        <td className="desc-cell">{opt.desc}</td>
                                        <td className="template-cell">
                                            <button
                                                className="icon-action-btn"
                                                onClick={() => downloadTemplate(opt.id)}
                                                aria-label={`Download ${opt.label} template`}
                                            >
                                                <FaDownload />
                                            </button>
                                        </td>
                                        <td className="action-cell">
                                            <button
                                                className="icon-primary-btn"
                                                onClick={() => handleOpenImport(opt.id)}
                                                aria-label={`Import ${opt.label}`}
                                            >
                                                <FaCloudUploadAlt />
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedPreview === 'students' && opt.id === 'students' && (
                                        <tr className="template-preview-row">
                                            <td colSpan={4}>
                                                <div className="template-preview-wrap">
                                                    <h4>Template Columns Preview</h4>
                                                    <div className="preview-chips">
                                                        {STUDENTS_TEMPLATE_PREVIEW.map((col) => (
                                                            <span key={col.label} className={`preview-chip ${col.required ? 'required' : ''}`}>
                                                                {col.required ? '● ' : ''}{col.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    <p><strong>* Required</strong> columns must be filled. Others are optional.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            {/* IMPORT MODAL */}
            {isModalOpen && (
                <div className="import-modal-overlay">
                    <div className="import-modal">
                        <div className="modal-header">
                            <h3>Import {IMPORT_OPTIONS.find(o => o.id === importType)?.label}</h3>
                            <button className="close-btn" onClick={handleCloseModal}>&times;</button>
                        </div>

                        <div className="modal-body">
                            {uploadStatus === 'complete' ? (
                                <div className="result-view">
                                    <div className="stats-summary">
                                        <div className="stat success">
                                            <strong>{stats?.success || 0}</strong> Imported
                                        </div>
                                        <div className="stat warning">
                                            <strong>{stats?.duplicates || 0}</strong> Duplicates
                                        </div>
                                        <div className="stat error">
                                            <strong>{stats?.fail || stats?.failed || 0}</strong> Failed
                                        </div>
                                    </div>

                                    {stats?.errors?.length > 0 && (
                                        <div className="error-list-mini">
                                            <p>Errors (Last 5):</p>
                                            <ul>
                                                {stats.errors.slice(-5).map((e, i) => (
                                                    <li key={i}>Row {e.row}: {e.reason}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    <div className="modal-footer">
                                        <button className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                                        <button className="btn btn-success" onClick={() => {
                                            if (importType === 'classes') navigate('/classes');
                                            else if (importType === 'courses') navigate('/courses');
                                            else if (importType === 'students') navigate('/students', { state: { selectedClass: selectedClassId } });
                                            else if (importType === 'faculty') navigate('/faculty');
                                            else if (importType === 'staff') navigate('/staff');
                                            else if (importType === 'fees') navigate('/finance');
                                        }}>
                                            <FaEye /> View List
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* ===== STEP 2: FILE UPLOAD (all types) ===== */
                                <>
                                    {importType !== 'students' && FIELD_GUIDE[importType] && (
                                        <div className="field-guide-container">
                                            <div className="field-guide-title">
                                                <FaExclamationCircle /> Template Format Guide
                                            </div>
                                            <div className="field-grid">
                                                {FIELD_GUIDE[importType].map((f, i) => (
                                                    <div key={i} className={`field-tag ${f.mandatory ? 'highlight' : ''}`}>
                                                        {f.label}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {importType === 'fees' && (
                                        <div className="modal-form-group">
                                            <label>Select Class:</label>
                                            <select
                                                className="modal-select"
                                                value={selectedClassId}
                                                onChange={(e) => setSelectedClassId(e.target.value)}
                                            >
                                                <option value="">-- Choose Class --</option>
                                                {classes.map(c => <option key={c.classId} value={c.classId}>{c.className} {c.sectionName}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    <div className="modal-upload-zone" onClick={() => document.getElementById('fileInput').click()}>
                                        <input
                                            id="fileInput"
                                            type="file"
                                            accept={importType === 'students' ? '.csv,.xlsx,.xls' : '.csv'}
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        {file ? (
                                            <div className="selected-file">
                                                <FaFileCsv />
                                                <span>{file.name}</span>
                                            </div>
                                        ) : (
                                            <div className="upload-prompt">
                                                <FaCloudUploadAlt />
                                                <p>{importType === 'students' ? 'Click to select CSV or Excel file' : 'Click to select CSV file'}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="modal-footer">
                                        <button className="btn btn-secondary" onClick={handleCloseModal} disabled={uploadStatus === 'processing'}>Cancel</button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleUpload}
                                            disabled={!file || uploadStatus === 'processing' || ((importType === 'fees') && !selectedClassId)}
                                        >
                                            {uploadStatus === 'processing' ? <Loader2 className="animate-spin" /> : 'Start Import'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Student Bulk Import — dedicated premium modal */}
            {studentModalOpen && (
                <StudentBulkImport
                    classes={classes}
                    orgId={orgId}
                    onClose={() => setStudentModalOpen(false)}
                    onImportComplete={(stats, viewStudents) => {
                        if (viewStudents) {
                            setStudentModalOpen(false);
                            navigate('/students');
                        }
                    }}
                />
            )}
        </div>
    );
}

export default BulkImportHub;
